/* ============================================================
   DA WORKSHOP SERVICES
   VERSION 2.0

   DESKTOP
   - Top category navigation
   - Floating information panel
   - Animated panel height
   - Directional content transitions

   MOBILE
   - Sticky category picker
   - Panel begins halfway down viewport
   - Natural page scrolling until docking point
   - Long panels dock 14px beneath picker
   - Internal panel scrolling while docked
   - Scroll chaining toward footer
   - Short panels do not force extra page height

   SHARED
   - Responsive background images
   - Image crossfades
   - URL history
   - Shopify theme editor support
   ============================================================ */

(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 749;

  const CONTENT_EXIT_DURATION = 220;
  const IMAGE_TRANSITION_DURATION = 650;
  const PANEL_HEIGHT_DURATION = 420;

  const instances = new WeakMap();


  /* ==========================================================
     INITIALIZATION
     ========================================================== */

  function initDAWorkshop(root) {
    if (!root || root.dataset.initialized === 'true') return;

    const dataElement = root.querySelector('[data-workshop-data]');

    if (!dataElement) return;

    let data;

    try {
      data = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error('DA Workshop: Invalid JSON data.', error);
      return;
    }

    const panel = root.querySelector('[data-workshop-panel]');
    const panelRegion = root.querySelector('[data-panel-region]');
    const body = root.querySelector('[data-panel-body]');
    const landing = root.querySelector('[data-workshop-landing]');

    const picker = root.querySelector('[data-mobile-picker]');
    const pickerButton = root.querySelector('[data-picker-button]');
    const pickerMenu = root.querySelector('[data-picker-menu]');
    const pickerLabel = root.querySelector('[data-picker-label]');

    const images = Array.from(
      root.querySelectorAll('.DAWorkshop__image')
    );

    if (
      !panel ||
      !panelRegion ||
      !body ||
      !landing ||
      !picker ||
      !pickerButton ||
      !pickerMenu ||
      !pickerLabel ||
      images.length < 2
    ) {
      console.error('DA Workshop: Required elements missing.');
      return;
    }

    root.dataset.initialized = 'true';


    /* ========================================================
       STATE
       ======================================================== */

    let activeCategory = null;
    let activeService = null;
    let currentView = 'landing';

    let renderGeneration = 0;
    let renderTimer = null;
    let renderFrame = null;

    let imageGeneration = 0;
    let imageTimer = null;
    let currentImage = '';
    let activeImageIndex = 0;

    let heightTimer = null;
    let heightFrame = null;

    let scrollFrame = null;
    let scrollResetFrame = null;

    /*
      Mobile docking state.

      normal:
        Panel participates in document flow.

      docked:
        Panel is fixed beneath the category navigation.

      released:
        Panel has completed its internal scroll and
        normal document scrolling resumes.
    */

    let mobilePanelState = 'normal';

    let dockingScrollY = 0;
    let releaseScrollY = 0;

    let panelPlaceholder = null;

    let lastScrollY = window.scrollY;

    let wheelHandlerAttached = false;

    const imageLayers = images.map(element => ({
      element,
      src: ''
    }));

    const mobileQuery = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT}px)`
    );

    const motionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    const isMobile = () => mobileQuery.matches;
    const reducedMotion = () => motionQuery.matches;


    /* ========================================================
       UTILITIES
       ======================================================== */

    const escapeHtml = value =>
      String(value ?? '').replace(/[&<>"']/g, character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        '>': '&gt;',
        "'": '&#39;'
      }[character]));

    const categoryFor = key =>
      (data.categories || []).find(item => item.key === key);

    const serviceFor = (categoryKey, serviceKey) =>
      (data.services || []).find(item =>
        item.parent === categoryKey &&
        item.key === serviceKey
      );

    const safeLink = url =>
      typeof url === 'string' &&
      (
        (/^\//.test(url) && !/^\/\//.test(url)) ||
        /^https?:\/\//i.test(url)
      )
        ? url
        : '';

    function getPanelStop() {
      const styles = getComputedStyle(root);

      const pickerHeight =
        parseFloat(
          styles.getPropertyValue('--daw-picker-height')
        ) || 62;

      const panelGap =
        parseFloat(
          styles.getPropertyValue('--daw-panel-gap')
        ) || 14;

      return pickerHeight + panelGap;
    }

    function getPanelMaximumHeight() {
      return Math.max(
        160,
        window.innerHeight - getPanelStop() - 14
      );
    }


    /* ========================================================
       RESPONSIVE IMAGES
       ======================================================== */

    function getResponsiveImage(item) {
      if (!item) return '';

      if (isMobile()) {
        return item.mobileImage || item.image || '';
      }

      return item.image || '';
    }

    function getLandingImage() {
      if (isMobile()) {
        return data.landingMobileImage ||
          data.landingImage ||
          '';
      }

      return data.landingImage || '';
    }


    /* ========================================================
       HTML HELPERS
       ======================================================== */

    function linkHtml(item) {
      const href = safeLink(item.link);

      if (!href) return '';

      return `
        <a
          class="DAWorkshop__link"
          href="${escapeHtml(href)}"
        >
          ${escapeHtml(item.linkLabel || 'EXPLORE')}
          <span aria-hidden="true">↗</span>
        </a>
      `;
    }

    function descriptionHtml(item) {
      return `
        <p class="DAWorkshop__description">
          ${escapeHtml(item.description)}
        </p>
      `;
    }

    function headingHtml(item) {
      return `
        <p class="DAWorkshop__count">
          ${escapeHtml(item.number)}
        </p>

        <h2 class="DAWorkshop__panelTitle">
          ${escapeHtml(item.title)}
        </h2>

        <span class="DAWorkshop__rule"></span>
      `;
    }


    /* ========================================================
       CATEGORY OVERVIEW
       ======================================================== */

    function categoryHtml(category) {
      const services = (data.services || []).filter(
        service => service.parent === category.key
      );

      return (
        headingHtml(category) +
        descriptionHtml(category) +
        `
          <div class="DAWorkshop__serviceList">

            ${services.map((service, index) => `
              <button
                type="button"
                class="DAWorkshop__serviceButton"
                data-service="${escapeHtml(service.key)}"
                aria-label="Explore ${escapeHtml(service.label)}"
              >
                <span>
                  ${escapeHtml(
                    service.number ||
                    String(index + 1).padStart(2, '0')
                  )}
                </span>

                <span>
                  ${escapeHtml(service.label)}
                </span>

                <span aria-hidden="true"></span>
              </button>
            `).join('')}

          </div>
        ` +
        linkHtml(category)
      );
    }


    /* ========================================================
       SERVICE DETAIL
       ======================================================== */

    function serviceHtml(category, service) {
      const details = [1, 2, 3].filter(index =>
        service[`detail${index}Label`] ||
        service[`detail${index}`]
      );

      const backNavigation = `
        <button
          type="button"
          class="DAWorkshop__parentLabel"
          data-collapse
          aria-label="Return to ${escapeHtml(category.title)}"
        >
          ${escapeHtml(category.number)} /
          ${escapeHtml(category.title)}
        </button>
      `;

      const detailGrid = details.length
        ? `
          <div class="DAWorkshop__detailGrid">

            ${details.map(index => `
              <div>
                <strong>
                  ${escapeHtml(service[`detail${index}Label`])}
                </strong>

                <p>
                  ${escapeHtml(service[`detail${index}`])}
                </p>
              </div>
            `).join('')}

          </div>
        `
        : '';

      return (
        backNavigation +
        headingHtml(service) +
        descriptionHtml(service) +
        detailGrid +
        linkHtml(service)
      );
    }


    /* ========================================================
       BACKGROUND IMAGE CROSSFADE
       ======================================================== */

    function setImage(src) {
      src = src || '';

      if (src === currentImage) return;

      currentImage = src;

      const generation = ++imageGeneration;

      if (imageTimer) {
        clearTimeout(imageTimer);
        imageTimer = null;
      }

      if (!src) {
        imageLayers.forEach(layer => {
          layer.element.classList.remove('is-visible');
        });

        return;
      }

      const preload = new Image();

      preload.onload = () => {
        if (generation !== imageGeneration) return;

        const nextIndex = 1 - activeImageIndex;

        const incoming = imageLayers[nextIndex];
        const outgoing = imageLayers[activeImageIndex];

        incoming.element.style.backgroundImage =
          `url(${JSON.stringify(src)})`;

        incoming.element.style.zIndex = '1';
        outgoing.element.style.zIndex = '0';

        incoming.element.classList.remove('is-visible');

        void incoming.element.offsetWidth;

        requestAnimationFrame(() => {
          if (generation !== imageGeneration) return;

          incoming.element.classList.add('is-visible');

          imageTimer = window.setTimeout(() => {
            if (generation !== imageGeneration) return;

            outgoing.element.classList.remove('is-visible');

            activeImageIndex = nextIndex;
            imageTimer = null;
          }, reducedMotion() ? 0 : IMAGE_TRANSITION_DURATION);
        });
      };

      preload.onerror = () => {
        if (generation === imageGeneration) {
          console.warn(
            'DA Workshop: Image failed to load.',
            src
          );
        }
      };

      preload.src = src;
    }


    /* ========================================================
       URL HISTORY
       ======================================================== */

    function updateURL(category, service) {
      const hash = category
        ? `#workshop/${encodeURIComponent(category)}${
            service ? '/' + encodeURIComponent(service) : ''
          }`
        : '#workshop';

      if (location.hash !== hash) {
        history.pushState(
          { daWorkshop: true },
          '',
          hash
        );
      }
    }


    /* ========================================================
       CATEGORY PICKER
       ======================================================== */

    function closePicker() {
      pickerMenu.hidden = true;
      pickerButton.setAttribute('aria-expanded', 'false');
    }

    function updateNavigation(categoryKey) {
      root.querySelectorAll('[data-category]').forEach(button => {
        const isActive =
          button.dataset.category === (categoryKey || '');

        button.classList.toggle('is-active', isActive);

        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }


    /* ========================================================
       MOBILE PANEL PLACEHOLDER
       ======================================================== */

    /*
      When the panel becomes fixed, it leaves normal
      document flow.

      A temporary placeholder prevents the layout
      from collapsing.

      This placeholder matches the panel's visible
      dimensions. It does not add artificial page
      height beyond the panel's original footprint.
    */

    function createPanelPlaceholder() {
      if (panelPlaceholder) return;

      panelPlaceholder = document.createElement('div');

      panelPlaceholder.className =
        'DAWorkshop__panelPlaceholder';

      panelPlaceholder.style.width = '100%';
      panelPlaceholder.style.height =
        `${panel.getBoundingClientRect().height}px`;

      panelPlaceholder.style.pointerEvents = 'none';

      panelRegion.insertBefore(
        panelPlaceholder,
        panel
      );
    }

    function removePanelPlaceholder() {
      if (!panelPlaceholder) return;

      panelPlaceholder.remove();
      panelPlaceholder = null;
    }


    /* ========================================================
       MOBILE PANEL STATE RESET
       ======================================================== */

    function resetMobilePanelState() {
      mobilePanelState = 'normal';

      dockingScrollY = 0;
      releaseScrollY = 0;

      root.classList.remove(
        'is-panel-docked',
        'is-panel-released'
      );

      panel.style.position = '';
      panel.style.top = '';
      panel.style.left = '';
      panel.style.right = '';
      panel.style.bottom = '';
      panel.style.width = '';
      panel.style.maxHeight = '';
      panel.style.transform = '';

      body.style.overflowY = '';
      body.scrollTop = 0;

      removePanelPlaceholder();
    }


    /* ========================================================
       MOBILE PANEL DOCKING
       ======================================================== */

    function dockMobilePanel() {
      if (
        !isMobile() ||
        !activeCategory ||
        mobilePanelState !== 'normal'
      ) {
        return;
      }

      /*
        Only dock panels whose content genuinely exceeds
        the available viewport height.
      */

      const maxHeight = getPanelMaximumHeight();

      const panelContentHeight =
        body.scrollHeight +
        panel.offsetHeight -
        body.clientHeight;

      if (panelContentHeight <= maxHeight + 2) {
        return;
      }

      dockingScrollY = window.scrollY;

      createPanelPlaceholder();

      const rect = panel.getBoundingClientRect();

      panel.style.position = 'fixed';
      panel.style.top = `${getPanelStop()}px`;
      panel.style.left = `${rect.left}px`;
      panel.style.right = 'auto';
      panel.style.bottom = 'auto';
      panel.style.width = `${rect.width}px`;
      panel.style.maxHeight = `${maxHeight}px`;
      panel.style.transform = 'none';

      root.classList.add('is-panel-docked');

      mobilePanelState = 'docked';
    }


    /* ========================================================
       RELEASE DOCKED PANEL
       ======================================================== */

    function releaseMobilePanel() {
      if (mobilePanelState !== 'docked') return;

      releaseScrollY = window.scrollY;

      root.classList.remove('is-panel-docked');
      root.classList.add('is-panel-released');

      panel.style.position = 'absolute';
      panel.style.top = `${
        getPanelStop() +
        releaseScrollY -
        (
          panelRegion.getBoundingClientRect().top +
          window.scrollY
        )
      }px`;

      panel.style.left = '16px';
      panel.style.right = '16px';
      panel.style.bottom = 'auto';
      panel.style.width = 'auto';
      panel.style.transform = 'none';

      body.style.overflowY = 'hidden';

      mobilePanelState = 'released';
    }


    /* ========================================================
       RESTORE DOCKED PANEL
       ======================================================== */

    function restoreDockedPanel() {
      if (mobilePanelState !== 'released') return;

      root.classList.remove('is-panel-released');
      root.classList.add('is-panel-docked');

      const rect = panelPlaceholder
        ? panelPlaceholder.getBoundingClientRect()
        : panelRegion.getBoundingClientRect();

      panel.style.position = 'fixed';
      panel.style.top = `${getPanelStop()}px`;
      panel.style.left = `${rect.left + 16}px`;
      panel.style.right = 'auto';
      panel.style.bottom = 'auto';
      panel.style.width = `${Math.max(
        0,
        rect.width - 32
      )}px`;

      panel.style.maxHeight =
        `${getPanelMaximumHeight()}px`;

      panel.style.transform = 'none';

      body.style.overflowY = 'auto';

      mobilePanelState = 'docked';
    }


    /* ========================================================
       MOBILE SCROLL STATE
       ======================================================== */

    function updateMobilePanelScroll() {
      if (!isMobile() || !activeCategory) return;

      const currentScrollY = window.scrollY;

      const scrollingDown =
        currentScrollY > lastScrollY;

      const scrollingUp =
        currentScrollY < lastScrollY;

      const stop = getPanelStop();

      if (mobilePanelState === 'normal') {

        const rect = panel.getBoundingClientRect();

        if (
          scrollingDown &&
          rect.top <= stop + 1
        ) {
          dockMobilePanel();
        }

      } else if (mobilePanelState === 'docked') {

        /*
          Restore natural positioning when scrolling
          upward beyond the original docking point.
        */

        if (
          scrollingUp &&
          currentScrollY < dockingScrollY - 2
        ) {
          resetMobilePanelState();
        }

      } else if (mobilePanelState === 'released') {

        /*
          Re-enter the docked state when scrolling
          upward toward the Workshop.
        */

        if (
          scrollingUp &&
          currentScrollY <= releaseScrollY
        ) {
          restoreDockedPanel();
        }

      }

      lastScrollY = currentScrollY;
    }


    /* ========================================================
       WHEEL SCROLL CHAINING
       ======================================================== */

    /*
      While docked, wheel scrolling is directed into
      the panel body.

      At the end of the content, the panel releases
      and normal document scrolling resumes.

      Touch scrolling remains native.
    */

    function handleWheel(event) {
      if (
        !isMobile() ||
        mobilePanelState !== 'docked'
      ) {
        return;
      }

      if (event.target.closest('[data-picker-menu]')) {
        return;
      }

      const delta = event.deltaY;

      const atBottom =
        body.scrollTop + body.clientHeight >=
        body.scrollHeight - 2;

      const atTop =
        body.scrollTop <= 2;

      if (delta > 0 && atBottom) {
        releaseMobilePanel();
        return;
      }

      if (delta < 0 && atTop) {
        resetMobilePanelState();
        return;
      }

      event.preventDefault();

      body.scrollTop += delta;
    }

    function attachWheelHandler() {
      if (wheelHandlerAttached) return;

      root.addEventListener(
        'wheel',
        handleWheel,
        { passive: false }
      );

      wheelHandlerAttached = true;
    }

    attachWheelHandler();


    /* ========================================================
       STICKY PICKER STATE
       ======================================================== */

    function updateStickyPicker() {
      if (!isMobile()) {
        picker.classList.remove('is-scrolled');
        return;
      }

      const rootTop =
        root.getBoundingClientRect().top;

      picker.classList.toggle(
        'is-scrolled',
        rootTop < -2
      );
    }


    /* ========================================================
       SCROLL UPDATE SCHEDULER
       ======================================================== */

    function updateScrollState() {
      scrollFrame = null;

      updateStickyPicker();
      updateMobilePanelScroll();
    }

    function scheduleScrollUpdate() {
      if (scrollFrame) return;

      scrollFrame = requestAnimationFrame(
        updateScrollState
      );
    }

    window.addEventListener(
      'scroll',
      scheduleScrollUpdate,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      scheduleScrollUpdate,
      { passive: true }
    );


    /* ========================================================
       MOBILE NAVIGATION SCROLL RESET
       ======================================================== */

    function resetMobileWorkshopScroll() {
      if (!isMobile()) return;

      if (scrollResetFrame) {
        cancelAnimationFrame(scrollResetFrame);
      }

      resetMobilePanelState();

      scrollResetFrame = requestAnimationFrame(() => {
        const workshopTop =
          root.getBoundingClientRect().top +
          window.scrollY;

        window.scrollTo({
          top: Math.max(0, workshopTop),
          behavior: 'instant'
        });

        lastScrollY = window.scrollY;

        scrollResetFrame = null;

        scheduleScrollUpdate();
      });
    }


    /* ========================================================
       DESKTOP PANEL HEIGHT ANIMATION
       ======================================================== */

    function clearHeightAnimation() {
      if (heightTimer) {
        clearTimeout(heightTimer);
        heightTimer = null;
      }

      if (heightFrame) {
        cancelAnimationFrame(heightFrame);
        heightFrame = null;
      }

      panel.style.height = '';
    }

    function animatePanelHeight(updateContent) {
      if (isMobile() || reducedMotion()) {
        clearHeightAnimation();
        updateContent();
        return;
      }

      clearHeightAnimation();

      const startHeight =
        panel.getBoundingClientRect().height;

      panel.style.height = `${startHeight}px`;

      updateContent();

      panel.style.height = '';

      const endHeight =
        panel.getBoundingClientRect().height;

      panel.style.height = `${startHeight}px`;

      void panel.offsetHeight;

      heightFrame = requestAnimationFrame(() => {
        panel.style.height = `${endHeight}px`;

        heightTimer = window.setTimeout(() => {
          panel.style.height = '';
          heightTimer = null;
        }, PANEL_HEIGHT_DURATION + 30);

        heightFrame = null;
      });
    }


    /* ========================================================
       CORE CONTENT RENDERING
       ======================================================== */

    function renderContent(
      categoryKey,
      serviceKey,
      updateHistory = true,
      animateHeight = false,
      resetScroll = false
    ) {
      const category = categoryFor(categoryKey);

      const service = category
        ? serviceFor(category.key, serviceKey)
        : null;

      resetMobilePanelState();

      activeCategory = category?.key || null;
      activeService = service?.key || null;

      currentView = !category
        ? 'landing'
        : service
          ? 'service'
          : 'category';

      root.classList.toggle(
        'has-category',
        Boolean(category)
      );

      panel.setAttribute(
        'aria-hidden',
        category ? 'false' : 'true'
      );

      landing.setAttribute(
        'aria-hidden',
        category ? 'true' : 'false'
      );

      pickerLabel.textContent = category
        ? category.nav
        : root.dataset.mobileLabel ||
          'EXPLORE WORKSHOP SERVICES';

      updateNavigation(activeCategory);
      closePicker();

      const updateBody = () => {

        if (!category) {

          body.innerHTML = '';
          setImage(getLandingImage());

        } else if (!service) {

          body.innerHTML = categoryHtml(category);

          setImage(
            getResponsiveImage(category) ||
            getLandingImage()
          );

        } else {

          body.innerHTML = serviceHtml(category, service);

          setImage(
            getResponsiveImage(service) ||
            getResponsiveImage(category) ||
            getLandingImage()
          );

        }

      };

      if (
        animateHeight &&
        category &&
        !isMobile()
      ) {
        animatePanelHeight(updateBody);
      } else {
        clearHeightAnimation();
        updateBody();
      }

      if (updateHistory) {
        updateURL(activeCategory, activeService);
      }

      if (resetScroll) {
        resetMobileWorkshopScroll();
      }

      scheduleScrollUpdate();
    }


    /* ========================================================
       CONTENT TRANSITIONS
       ======================================================== */

    function clearTransitions() {
      if (renderTimer) {
        clearTimeout(renderTimer);
        renderTimer = null;
      }

      if (renderFrame) {
        cancelAnimationFrame(renderFrame);
        renderFrame = null;
      }

      body.classList.remove(
        'is-transitioning',
        'is-expanding',
        'is-collapsing',
        'is-entering-detail',
        'is-entering-category'
      );
    }

    function render(
      categoryKey,
      serviceKey,
      updateHistory = true,
      resetScroll = true
    ) {
      const generation = ++renderGeneration;

      const nextCategory = categoryFor(categoryKey);

      const nextService = nextCategory
        ? serviceFor(nextCategory.key, serviceKey)
        : null;

      const nextView = !nextCategory
        ? 'landing'
        : nextService
          ? 'service'
          : 'category';

      const previousView = currentView;
      const previousCategory = activeCategory;

      clearTransitions();

      if (
        reducedMotion() ||
        previousView === 'landing' ||
        nextView === 'landing'
      ) {
        renderContent(
          categoryKey,
          serviceKey,
          updateHistory,
          false,
          resetScroll
        );

        return;
      }

      const forward =
        previousView === 'category' &&
        nextView === 'service' &&
        previousCategory === nextCategory?.key;

      const backward =
        previousView === 'service' &&
        nextView === 'category' &&
        previousCategory === nextCategory?.key;

      let exitClass = 'is-transitioning';
      let enterClass = 'is-transitioning';

      if (forward) {
        exitClass = 'is-expanding';
        enterClass = 'is-entering-detail';
      }

      if (backward) {
        exitClass = 'is-collapsing';
        enterClass = 'is-entering-category';
      }

      body.classList.add(exitClass);

      if (resetScroll && isMobile()) {
        resetMobileWorkshopScroll();
      }

      renderTimer = window.setTimeout(() => {
        if (generation !== renderGeneration) return;

        renderContent(
          categoryKey,
          serviceKey,
          updateHistory,
          !isMobile(),
          false
        );

        body.classList.remove(exitClass);
        body.classList.add(enterClass);

        renderFrame = requestAnimationFrame(() => {
          renderFrame = requestAnimationFrame(() => {
            if (generation !== renderGeneration) return;

            body.classList.remove(enterClass);
            renderFrame = null;
          });
        });

        renderTimer = null;
      }, CONTENT_EXIT_DURATION);
    }


    /* ========================================================
       URL HISTORY
       ======================================================== */

    function fromHash() {
      const match = location.hash.match(
        /^#workshop(?:\/([^/]+))?(?:\/([^/]+))?$/
      );

      if (!match) return;

      let category = '';
      let service = '';

      try {
        category = decodeURIComponent(match[1] || '');
        service = decodeURIComponent(match[2] || '');
      } catch (error) {
        console.warn(
          'DA Workshop: Invalid URL encoding.',
          error
        );
      }

      render(
        category,
        service,
        false,
        false
      );
    }


    /* ========================================================
       EVENT HANDLING
       ======================================================== */

    function handleRootClick(event) {
      const categoryButton =
        event.target.closest('[data-category]');

      const serviceButton =
        event.target.closest('[data-service]');

      if (categoryButton) {
        render(
          categoryButton.dataset.category,
          null,
          true,
          true
        );

        return;
      }

      if (serviceButton && activeCategory) {
        render(
          activeCategory,
          serviceButton.dataset.service,
          true,
          true
        );

        return;
      }

      if (event.target.closest('[data-collapse]')) {
        render(
          activeCategory,
          null,
          true,
          true
        );

        return;
      }

      if (event.target.closest('[data-close]')) {
        render(
          null,
          null,
          true,
          true
        );

        return;
      }

      if (event.target.closest('[data-picker-button]')) {
        pickerMenu.hidden = !pickerMenu.hidden;

        pickerButton.setAttribute(
          'aria-expanded',
          String(!pickerMenu.hidden)
        );
      }
    }

    root.addEventListener(
      'click',
      handleRootClick
    );


    /* ========================================================
       OUTSIDE CLICK
       ======================================================== */

    function handleOutsideClick(event) {
      if (!root.isConnected) return;

      if (!picker.contains(event.target)) {
        closePicker();
      }
    }

    document.addEventListener(
      'click',
      handleOutsideClick
    );


    /* ========================================================
       KEYBOARD
       ======================================================== */

    function handleKeydown(event) {
      if (
        !root.isConnected ||
        event.key !== 'Escape'
      ) {
        return;
      }

      if (!pickerMenu.hidden) {
        closePicker();
        pickerButton.focus();
        return;
      }

      if (activeService) {
        render(activeCategory, null);
        return;
      }

      if (activeCategory) {
        render(null, null);
      }
    }

    document.addEventListener(
      'keydown',
      handleKeydown
    );


    /* ========================================================
       RESPONSIVE CHANGES
       ======================================================== */

    function handleModeChange() {
      resetMobilePanelState();
      clearHeightAnimation();
      clearTransitions();

      renderContent(
        activeCategory,
        activeService,
        false,
        false,
        false
      );

      scheduleScrollUpdate();
    }

    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener(
        'change',
        handleModeChange
      );
    } else {
      mobileQuery.addListener(handleModeChange);
    }


    /* ========================================================
       BROWSER HISTORY
       ======================================================== */

    function handlePopState() {
      if (location.hash.startsWith('#workshop')) {
        fromHash();
      } else {
        render(null, null, false, false);
      }
    }

    function handleHashChange() {
      if (location.hash.startsWith('#workshop')) {
        fromHash();
      }
    }

    window.addEventListener(
      'popstate',
      handlePopState
    );

    window.addEventListener(
      'hashchange',
      handleHashChange
    );


    /* ========================================================
       INITIAL STATE
       ======================================================== */

    resetMobilePanelState();

    if (location.hash.startsWith('#workshop')) {
      fromHash();
    } else {
      renderContent(
        null,
        null,
        false,
        false,
        false
      );
    }

    lastScrollY = window.scrollY;

    scheduleScrollUpdate();


    /* ========================================================
       CLEANUP
       ======================================================== */

    instances.set(root, {
      destroy() {
        resetMobilePanelState();
        clearTransitions();
        clearHeightAnimation();

        if (imageTimer) {
          clearTimeout(imageTimer);
        }

        if (scrollFrame) {
          cancelAnimationFrame(scrollFrame);
        }

        if (scrollResetFrame) {
          cancelAnimationFrame(scrollResetFrame);
        }

        root.removeEventListener(
          'click',
          handleRootClick
        );

        root.removeEventListener(
          'wheel',
          handleWheel
        );

        document.removeEventListener(
          'click',
          handleOutsideClick
        );

        document.removeEventListener(
          'keydown',
          handleKeydown
        );

        window.removeEventListener(
          'scroll',
          scheduleScrollUpdate
        );

        window.removeEventListener(
          'resize',
          scheduleScrollUpdate
        );

        window.removeEventListener(
          'popstate',
          handlePopState
        );

        window.removeEventListener(
          'hashchange',
          handleHashChange
        );

        if (mobileQuery.removeEventListener) {
          mobileQuery.removeEventListener(
            'change',
            handleModeChange
          );
        } else {
          mobileQuery.removeListener(handleModeChange);
        }

        root.dataset.initialized = 'false';
      }
    });
  }


  /* ==========================================================
     INITIALIZE WORKSHOP SECTIONS
     ========================================================== */

  function initAllDAWorkshops(scope = document) {
    const roots = [];

    if (
      scope.matches &&
      scope.matches('[data-da-workshop]')
    ) {
      roots.push(scope);
    }

    roots.push(
      ...scope.querySelectorAll('[data-da-workshop]')
    );

    roots.forEach(initDAWorkshop);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAllDAWorkshops(),
      { once: true }
    );
  } else {
    initAllDAWorkshops();
  }


  /* ==========================================================
     SHOPIFY THEME EDITOR
     ========================================================== */

  document.addEventListener(
    'shopify:section:load',
    event => {
      initAllDAWorkshops(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    event => {
      const roots = [];

      if (
        event.target.matches &&
        event.target.matches('[data-da-workshop]')
      ) {
        roots.push(event.target);
      }

      roots.push(
        ...event.target.querySelectorAll('[data-da-workshop]')
      );

      roots.forEach(root => {
        const instance = instances.get(root);

        if (instance) {
          instance.destroy();
          instances.delete(root);
        }
      });
    }
  );

})();